// Pure equip rules over a Loadout (Phaser-free). The inventory wraps one
// mutable Loadout; comparisons run the same rules on a clone so "what would
// happen if I equipped this" never diverges from what equipping really does.

import type { ItemInstance, MajorUpgradeId, ModInstance, WeaponInstance } from '../../core/types';
import { MOD_SLOTS_BY_RARITY } from './items';
import type { Loadout } from './stats';

export const MAX_MUTATIONS = 6;
export const WEAPON_SLOTS = 2;

export interface EquipOptions {
  /** Weapons: slot to replace when both are full. Mods: weapon to install on. */
  weaponSlot?: number;
  /** Mods: which installed mod to replace when the weapon is full (default 0). */
  modIndex?: number;
}

export function hasModSlotUpgrade(upgrades: readonly MajorUpgradeId[]): boolean {
  return upgrades.includes('mod_slot');
}

/**
 * Mod slots a weapon should have given its rarity and the MOD_SLOT upgrade.
 * Idempotent normalisation: never shrinks a weapon that already has more.
 */
export function normalizeModSlots(w: WeaponInstance, upgrades: readonly MajorUpgradeId[]): void {
  const want = (MOD_SLOTS_BY_RARITY[w.rarity] ?? 1) + (hasModSlotUpgrade(upgrades) ? 1 : 0);
  if (w.modSlots < want) w.modSlots = want;
}

/** Index of the weapon mods go onto: the active weapon, else the first weapon held. */
export function modTargetSlot(l: Loadout, weaponSlot?: number): number {
  if (weaponSlot !== undefined && l.weapons[weaponSlot]) return weaponSlot;
  if (l.weapons[l.activeWeapon]) return l.activeWeapon;
  const i = l.weapons.findIndex((w) => !!w);
  return i;
}

export function freeModSlots(w: WeaponInstance | null | undefined): number {
  return w ? Math.max(0, w.modSlots - w.mods.length) : 0;
}

/** Empty weapon slot index or -1. */
export function emptyWeaponSlot(l: Loadout): number {
  return l.weapons.findIndex((w) => !w);
}

/** True if equipping would not displace anything (empty slot, free mod slot, mutation). */
export function canAutoEquip(l: Loadout, item: ItemInstance): boolean {
  switch (item.kind) {
    case 'weapon':
      return emptyWeaponSlot(l) >= 0;
    case 'equipment':
      return !l.equipment[item.slot];
    case 'mod': {
      const slot = modTargetSlot(l);
      return slot >= 0 && freeModSlots(l.weapons[slot]) > 0;
    }
    case 'mutation':
      return true;
  }
}

/** The equipped item `equip` would replace (null = goes into a free spot). */
export function replacedBy(l: Loadout, item: ItemInstance, opts: EquipOptions = {}): ItemInstance | null {
  switch (item.kind) {
    case 'weapon': {
      if (emptyWeaponSlot(l) >= 0) return null;
      const slot = clampSlot(opts.weaponSlot ?? l.activeWeapon);
      return l.weapons[slot] ?? null;
    }
    case 'equipment':
      return l.equipment[item.slot] ?? null;
    case 'mod': {
      const slot = modTargetSlot(l, opts.weaponSlot);
      const w = slot >= 0 ? l.weapons[slot] : null;
      if (!w || freeModSlots(w) > 0 || w.mods.length === 0) return null;
      return w.mods[clampIndex(opts.modIndex ?? 0, w.mods.length)] ?? null;
    }
    case 'mutation':
      return l.mutations.length >= MAX_MUTATIONS ? l.mutations[0] ?? null : null;
  }
}

/** True if an instance (by uid) is already part of the loadout. */
export function containsUid(l: Loadout, uid: string): boolean {
  for (const w of l.weapons) {
    if (!w) continue;
    if (w.uid === uid) return true;
    if (w.mods.some((m) => m.uid === uid)) return true;
  }
  if (Object.values(l.equipment).some((e) => e?.uid === uid)) return true;
  return l.mutations.some((m) => m.uid === uid);
}

/**
 * Equip `item` into the loadout (mutates it). Returns the displaced item so
 * the caller can drop it nearby (reversible swaps).
 * - weapons: empty slot, else replace the active weapon (or weaponSlot); the new weapon becomes active
 * - equipment: its slot
 * - mods: onto the active weapon if it has a free slot, else replace mods[modIndex ?? 0]
 * - mutations: append; when 6 are owned the oldest is replaced and returned
 */
export function applyEquip(l: Loadout, item: ItemInstance, opts: EquipOptions = {}): ItemInstance | null {
  if (containsUid(l, item.uid)) return null;
  switch (item.kind) {
    case 'weapon': {
      normalizeModSlots(item, l.upgrades);
      const empty = emptyWeaponSlot(l);
      if (empty >= 0) {
        l.weapons[empty] = item;
        l.activeWeapon = empty;
        return null;
      }
      const slot = clampSlot(opts.weaponSlot ?? l.activeWeapon);
      const old = l.weapons[slot];
      l.weapons[slot] = item;
      l.activeWeapon = slot;
      return old ?? null;
    }
    case 'equipment': {
      const old = l.equipment[item.slot];
      l.equipment[item.slot] = item;
      return old ?? null;
    }
    case 'mod': {
      const slot = modTargetSlot(l, opts.weaponSlot);
      const w = slot >= 0 ? l.weapons[slot] : null;
      // No weapon to hold the mod: hand it straight back so the caller drops it.
      if (!w) return item;
      if (freeModSlots(w) > 0) {
        w.mods.push(item);
        return null;
      }
      const idx = clampIndex(opts.modIndex ?? 0, w.mods.length);
      const old = w.mods[idx];
      w.mods[idx] = item;
      return old ?? null;
    }
    case 'mutation': {
      let old: ItemInstance | null = null;
      if (l.mutations.length >= MAX_MUTATIONS) old = l.mutations.shift() ?? null;
      l.mutations.push(item);
      return old;
    }
  }
}

/** Remove a mod from a weapon; returns it. */
export function removeModFrom(l: Loadout, weaponSlot: number, modIndex: number): ModInstance | null {
  const w = l.weapons[weaponSlot];
  if (!w || modIndex < 0 || modIndex >= w.mods.length) return null;
  return w.mods.splice(modIndex, 1)[0] ?? null;
}

/** Deep-enough copy for simulation: arrays and weapon instances are cloned, leaf items shared. */
export function cloneLoadout(l: Loadout): Loadout {
  return {
    weapons: l.weapons.map((w) => (w ? { ...w, mods: [...w.mods] } : null)),
    activeWeapon: l.activeWeapon,
    equipment: { ...l.equipment },
    mutations: [...l.mutations],
    upgrades: [...l.upgrades],
  };
}

/** Clone an item for simulation so normalisation never touches the real instance. */
export function cloneItem<T extends ItemInstance>(item: T): T {
  if (item.kind === 'weapon') return { ...item, mods: [...item.mods] } as T;
  return { ...item };
}

function clampSlot(i: number): number {
  return Math.max(0, Math.min(WEAPON_SLOTS - 1, Math.floor(i)));
}

function clampIndex(i: number, len: number): number {
  return Math.max(0, Math.min(len - 1, Math.floor(i)));
}

/** Ids currently equipped (weapons, equipment, mutations — mods excluded: duplicates allowed). */
export function equippedIds(l: Loadout): Set<string> {
  const ids = new Set<string>();
  for (const w of l.weapons) if (w) ids.add(w.defId);
  for (const e of Object.values(l.equipment)) if (e) ids.add(e.defId);
  for (const m of l.mutations) ids.add(m.defId);
  return ids;
}
